import React, { useState } from "react";
import UpgradePanel from "./UpgradePanel";

// Only speak up near the end. Earlier than this it's nagging: they're still
// trying the product, which is the point of the trial.
const WARN_WITHIN_DAYS = 3;

/**
 * Notice that the free trial is nearly over.
 *
 * The trial is granted by us rather than by Stripe, so Stripe sends no
 * "your trial is ending" mail — without this the trial could simply stop
 * mid-session. Reads the subscription already on `currentUser`, so it costs
 * no request.
 */
export default function TrialEndingBanner({ user }) {
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const sub = user?.subscription;
  // `stripe: false` marks our own trial. A Stripe-backed trial is Stripe's to
  // chase, and it emails about it.
  if (dismissed || !sub || sub.status !== "trialing" || sub.stripe || !sub.trial_end) {
    return null;
  }

  const endsAt = new Date(sub.trial_end);
  if (isNaN(endsAt)) return null;

  const msLeft = endsAt - Date.now();
  if (msLeft <= 0) return null; // already over — the paywall has them
  const daysLeft = Math.ceil(msLeft / 86400000);
  if (daysLeft > WARN_WITHIN_DAYS) return null;

  const when =
    daysLeft === 1
      ? "Your free trial ends today"
      : `Your free trial ends in ${daysLeft} days`;

  return (
    <div
      role="status"
      className="shrink-0 px-4 py-2 text-sm"
      style={{
        background: "var(--gold-bg)",
        color: "var(--gold-bright)",
        borderBottom: "1px solid var(--panel-line)",
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <span>{when} — subscribe to keep your progress and carry on.</span>
        <span className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="underline underline-offset-2 hover:brightness-125 transition-[filter]"
          >
            {expanded ? "Not now" : "Subscribe"}
          </button>
          <button
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className="opacity-70 hover:opacity-100"
          >
            ✕
          </button>
        </span>
      </div>
      {/* Expands in place rather than sending them hunting through menus —
          they're already at the moment of deciding. */}
      {expanded && (
        <div className="max-w-sm mx-auto pt-3 pb-1">
          <UpgradePanel
            compact
            note="Your remaining free days are kept — billing starts when the trial would have ended."
          />
        </div>
      )}
    </div>
  );
}
